if ( typeof MainHeader !== 'function' ) {

	class MainHeader extends HTMLElement {

		constructor(){
			super();
			this.#mount();
		}

		#mount(){

			/* -- > DRAWERS < -- */

			if ( this.hasAttribute('data-sticky-header') ) {

				const stickyMedia = window.matchMedia('(min-width: 1024px)');
				const cartButton = this.querySelector('.button--cart-handle');
				const cartParent = cartButton ? cartButton.parentNode : null;
				const cartNextSibling = cartButton ? cartButton.nextSibling : null;
				const bottomActions = this.querySelector('[data-js-header-actions]');

				const restoreCart = ()=>{
					if ( ! cartButton || ! cartParent || cartButton.parentNode === cartParent ) return;
					if ( cartNextSibling && cartNextSibling.parentNode === cartParent ) {
						cartParent.insertBefore(cartButton, cartNextSibling);
					} else {
						cartParent.append(cartButton);
					}
				};

				const clearStickyTarget = ()=>{
					if ( this._stickyObserver ) this._stickyObserver.disconnect();
					if ( this._stickyResizeObserver ) this._stickyResizeObserver.disconnect();
					if ( this._setHeaderStickyState ) this._setHeaderStickyState(false);
					if ( this._stickySentinel ) this._stickySentinel.remove();
					if ( this._stickyPlaceholder ) this._stickyPlaceholder.remove();
					this._stickyObserver = null;
					this._stickyResizeObserver = null;
					this._stickySentinel = null;
					this._stickyPlaceholder = null;
					this._stickyTarget = null;
					this._setHeaderStickyState = null;
					this._stickyActive = false;
				};

				const mountStickyTarget = ()=>{
					clearStickyTarget();

					const target = this.querySelector(stickyMedia.matches ? '.header-container--bottom' : '.header-container--top');
					if ( ! target ) return;

					const sentinel = document.createElement('span');
					sentinel.className = 'header-sticky-sentinel';
					sentinel.setAttribute('aria-hidden', 'true');
					const placeholder = document.createElement('div');
					placeholder.className = 'header-sticky-placeholder';
					placeholder.setAttribute('aria-hidden', 'true');
					target.before(sentinel);
					target.after(placeholder);

					const updateStickyHeight = ()=>{
						const height = target.offsetHeight;
						this.style.setProperty('--header-sticky-row-height', `${height}px`);
						placeholder.style.height = `${height}px`;
					};

					const setStickyState = enabled=>{
						if ( this._stickyActive === enabled ) return;
						this._stickyActive = enabled;
						if ( enabled ) {
							updateStickyHeight();
							this.classList.add('is-header-sticky');
							placeholder.classList.add('is-active');
							target.classList.add('is-sticky-row');
							if ( stickyMedia.matches && cartButton && bottomActions ) bottomActions.append(cartButton);
						} else {
							target.classList.remove('is-sticky-row');
							placeholder.classList.remove('is-active');
							this.classList.remove('is-header-sticky');
							restoreCart();
						}
					};

					this._stickyObserver = new IntersectionObserver(entries=>{
						const entry = entries[0];
						setStickyState(! entry.isIntersecting && entry.boundingClientRect.top < 0);
					}, { threshold: 0 });
					this._stickyObserver.observe(sentinel);

					if ( typeof ResizeObserver === 'function' ) {
						this._stickyResizeObserver = new ResizeObserver(updateStickyHeight);
						this._stickyResizeObserver.observe(target);
					}

					this._stickySentinel = sentinel;
					this._stickyPlaceholder = placeholder;
					this._stickyTarget = target;
					this._setHeaderStickyState = setStickyState;
					updateStickyHeight();
				};

				this._stickyMedia = stickyMedia;
				this._stickyMediaChange = mountStickyTarget;
				this._restoreStickyCart = restoreCart;
				this._clearStickyTarget = clearStickyTarget;
				stickyMedia.addEventListener('change', this._stickyMediaChange);
				mountStickyTarget();

			}

			// drawer cart connections

			document.querySelectorAll('[data-js-sidebar-handle]').forEach(elm => {
				if ( elm.hasAttribute('aria-controls') ) {
					const elmSidebar = document.getElementById(elm.getAttribute('aria-controls'));
					elm.addEventListener('click', e=>{
						e.preventDefault();
						if ( ! elmSidebar ) return;
						window.lastFocusedElm = elm;
						elm.setAttribute('aria-expanded', 'true');
						elmSidebar.show();
						setTimeout(()=>elmSidebar.querySelector('[data-js-close]')?.focus(), 20);
					})
					elm.addEventListener('keyup', e=>{
						if ( e.keyCode === window.KEYCODES.RETURN ) {
							elm.setAttribute('aria-expanded', 'true');
							elmSidebar.show();
							window.lastFocusedElm = elm;
							elmSidebar.querySelector('[data-js-close]').focus();
						}
					})
				}
			})
			
			// closing drawers

			document.querySelectorAll('sidebar-drawer [data-js-close]').forEach(elm=>{
				elm.addEventListener('keydown', e=>{
					if ( e.keyCode === window.KEYCODES.RETURN ) {
						if ( window.lastFocusedElm ) {
							setTimeout(()=>{
								window.lastFocusedElm.focus();
								window.lastFocusedElm = null;
							}, 100);
						}
					}
				});
				elm.addEventListener('click', e=>{
					e.preventDefault();
					if ( e.target.closest('.sidebar').classList.contains('sidebar--opened') ) {
						e.target.closest('.sidebar').hide();
					}
				});
			});
			document.querySelector('.site-overlay').addEventListener('click', ()=>{
				if ( document.querySelector('.sidebar--opened') ) {
					document.querySelector('.sidebar--opened').hide();
				}
			});
			document.addEventListener('keydown', e=>{
				if ( e.keyCode === window.KEYCODES.ESC ) {
					if ( document.querySelector('.sidebar--opened') ) {
						document.querySelector('.sidebar--opened').hide();
						if ( window.lastFocusedElm ) {
							window.lastFocusedElm.focus();
							window.lastFocusedElm = null;
						}
					}
				}
			});

			// resizing drawers

			const rootHeight = document.getElementById('root-height');
			this.RESIZE_SidebarHelper = debounce(()=>{
				rootHeight.innerHTML = `:root {
					--window-height: ${window.innerHeight}px;
				}`;
			}, 200);
			window.addEventListener('resize', this.RESIZE_SidebarHelper);
			rootHeight.innerHTML = `:root {
				--window-height: ${window.innerHeight}px;
			}`;

			// Inert elements

			document.querySelectorAll('#main > div').forEach(elm=>{
				if ( ! elm.classList.contains('inert-inside') ) {
					elm.setAttribute('data-js-inert', '');
				}
			})
			window.inertElems = document.querySelectorAll('[data-js-inert]');

			// Init modal windows

			document.querySelectorAll('[aria-controls="modal-store-selector"]').forEach(elm=>{
				elm.addEventListener('keydown', e=>{
					if ( e.keyCode === window.KEYCODES.RETURN ) {
						window.lastFocusedElm = elm;
					}
				})
				elm.addEventListener('click', e=>{
					e.preventDefault();
					if ( document.querySelector('.sidebar--opened') ) {
						document.querySelector('.sidebar--opened').hide();
					}
					if ( document.getElementById(elm.getAttribute('aria-controls')) ) {
						document.getElementById(elm.getAttribute('aria-controls')).show();
					}
				})
				elm.addEventListener('keyup', e=>{
					if ( e.keyCode === window.KEYCODES.RETURN ) {
						if ( document.querySelector('.sidebar--opened') ) {
							document.querySelector('.sidebar--opened').hide();
						}
						if ( document.getElementById(elm.getAttribute('aria-controls')) ) {
							document.getElementById(elm.getAttribute('aria-controls')).show();
						}
					}
				})
			});

			// Submenu alignment

			const rtl = document.documentElement.getAttribute('dir') === 'rtl';
			document.querySelectorAll('.site-nav.style--classic .has-submenu').forEach(elm=>{
				elm.addEventListener('mouseover', ()=>{
					if ( ! elm.closest('.site-nav')?.classList.contains('style--classic') ) return;
					if ( elm.querySelector('.normal-menu') ) {
						elm.querySelector('.normal-menu').style.left = `${rtl ? elm.getBoundingClientRect().right : elm.getBoundingClientRect().left}px`;
					}
				})
			});

			// predictive search

			const bindPredictiveSearchInputListeners = elm=>{
				if ( ! elm ) {
					return;
				}
				const overlay = document.getElementById(elm.dataset.jsFocusOverlay);
				const resultsContainer = elm.closest('search-form')?.querySelector('[data-js-search-results]');
				const closePredictiveSearch = ()=>{
					overlay?.classList.remove('active');
					elm.setAttribute('aria-expanded', 'false');
				};
				elm.addEventListener('focus', ()=>{
					if ( overlay && resultsContainer?.innerHTML.trim() ) {
						overlay.classList.add('active');
						elm.setAttribute('aria-expanded', 'true');
					}
					if ( ! customElements.get('search-form') && ! document.body.classList.contains('predictive-script-loaded') ) {
						document.body.classList.add('predictive-script-loaded')
						const predictiveSearchJS = document.createElement('script');
						predictiveSearchJS.src = KROWN.settings.predictive_search_script;
						document.head.appendChild(predictiveSearchJS); 
					}
				})
				elm.addEventListener('keydown', e=>{
					if ( e.keyCode === window.KEYCODES.TAB || e.keyCode === window.KEYCODES.ESC ) {
						closePredictiveSearch();
					}
				})
				overlay?.addEventListener('click', closePredictiveSearch);
			};

			const predictiveSearchSupported = JSON.parse(document.getElementById('shopify-features').text).predictiveSearch;
			const predictiveSearchEnabled = KROWN.settings.predictive_search_enabled !== 'false';
			if ( predictiveSearchEnabled && predictiveSearchSupported ) {
				document.querySelectorAll('search-form [data-js-search-input]').forEach(bindPredictiveSearchInputListeners)
				const revealPredictiveSearch = ()=>{
					document.querySelectorAll('.search-results-overlay, .search-results-container').forEach(elm=>{
						elm.style = '';
					})
				};
				if ( document.readyState === 'complete' ) {
					revealPredictiveSearch();
				} else {
					window.addEventListener('load', revealPredictiveSearch, { once: true });
				}
			} else if ( predictiveSearchEnabled ) {
				document.querySelector('search-form [data-js-search-input] + button')?.classList.add('button--invisibile-trigger');
			}

			// mobile search: reveal on first click, focus on every click (independent per header)

			const mobileSearchSource = this.querySelector('[data-header-mobile-search-panel]');
			const mobileSearchToggles = document.querySelectorAll('[data-header-mobile-search-toggle]');
			if ( mobileSearchSource && mobileSearchToggles.length ) {

				let mainRevealed = false;

				const revealMainSearch = ()=>{
					if ( mainRevealed ) {
						return;
					}
					mainRevealed = true;
					mobileSearchSource.classList.remove('hide');
				};

				mobileSearchToggles.forEach(btn=>{
					btn.addEventListener('click', e=>{
						e.preventDefault();
						revealMainSearch();
						const input = mobileSearchSource.querySelector('input[type=search]');
						if ( input ) {
							input.focus();
						}
						btn.setAttribute('aria-expanded', 'true');
					});
				});
			}

			this._headerSearchQNameSync = e=>{
				if ( e.target.tagName !== 'INPUT' || e.target.name !== 'q' ) {
					return;
				}
				if ( ! e.target.closest('#site-header') ) {
					return;
				}
				const enterCode = window.KEYCODES ? window.KEYCODES.RETURN : 13;
				const enterInit = { key: 'Enter', code: 'Enter', keyCode: enterCode, which: enterCode, bubbles: true, cancelable: true };
				document.querySelectorAll('#site-header input[name="q"]').forEach(el=>{
					if ( el !== e.target ) {
						el.value = '';
						el.dispatchEvent(new KeyboardEvent('keydown', enterInit));
						el.dispatchEvent(new KeyboardEvent('keyup', enterInit));
					}
				});
			};
			document.body.addEventListener('input', this._headerSearchQNameSync);

			const desktopNavigationMedia = window.matchMedia('(min-width: 1024px)');
			const closeDesktopSubmenus = (exception=null)=>{
				this.querySelectorAll('.site-nav li.focus').forEach(item=>{
					if ( item === exception || item.contains(exception) ) return;
					item.classList.remove('focus');
					item.querySelector(':scope > .menu-item__trigger > .submenu-toggle')?.setAttribute('aria-expanded', 'false');
				});
			};

			this.querySelectorAll('.site-nav .submenu-toggle').forEach(button=>{
				const item = button.closest('li');
				button.addEventListener('click', e=>{
					if ( ! desktopNavigationMedia.matches ) return;
					e.preventDefault();
					const open = ! item.classList.contains('focus');
					closeDesktopSubmenus(open ? item : null);
					item.classList.toggle('focus', open);
					button.setAttribute('aria-expanded', open ? 'true' : 'false');
				});
				item.addEventListener('focusout', ()=>{
					if ( ! desktopNavigationMedia.matches ) return;
					setTimeout(()=>{
						if ( item.contains(document.activeElement) ) return;
						item.classList.remove('focus');
						button.setAttribute('aria-expanded', 'false');
					});
				});
			});

		}

		unmount(){
			window.removeEventListener('resize', this.RESIZE_SidebarHelper);
			if ( this._stickyMedia && this._stickyMediaChange ) {
				this._stickyMedia.removeEventListener('change', this._stickyMediaChange);
			}
			if ( this._clearStickyTarget ) this._clearStickyTarget();
			if ( this._restoreStickyCart ) this._restoreStickyCart();
			if ( this._headerSearchQNameSync ) {
				document.body.removeEventListener('input', this._headerSearchQNameSync);
			}
		}

	}
	
  if ( typeof customElements.get('main-header') === 'undefined' ) {
		customElements.define('main-header', MainHeader);
	}

}

if ( typeof SidebarDrawer !== 'function' ) {

	class SidebarDrawer extends HTMLElement {

		constructor(){
			super();
			this._inertedElements = [];
			this._navigationHideTimer = null;
			this.querySelector('[data-js-close]')?.addEventListener('click', ()=>{
				this.hide();
			});
			document.addEventListener('keydown', e=>{
				if ( e.keyCode === window.KEYCODES.ESC ) {
					const openedSidebar = document.querySelector('sidebar-drawer.sidebar--opened');
					if ( openedSidebar ){
						openedSidebar.hide();
					}
				}
			});

			if ( this.id === 'site-menu-sidebar' ) {
				this._navigationMedia = window.matchMedia('(min-width: 1024px)');
				this._navigationMediaChange = ()=>{
					if ( ! this._navigationMedia.matches ) return;
					clearTimeout(this._navigationHideTimer);
					this.#closeImmediately();
					this.style.display = 'none';
					this.setAttribute('aria-hidden', 'true');
					this.#restoreSharedNavigation();
				};
				this._navigationMedia.addEventListener('change', this._navigationMediaChange);
			}
		}

		#mountSharedNavigation(){
			if ( this.id !== 'site-menu-sidebar' ) return;
			const navigation = document.querySelector('[data-shared-navigation]');
			const target = this.querySelector('#MobileNavTarget');
			const mobileNavigation = this.querySelector('mobile-navigation');
			if ( ! navigation || ! target || ! mobileNavigation ) return;
			if ( target.contains(navigation) ) {
				mobileNavigation.mountNavigation(navigation);
				return;
			}

			this._sharedNavigationPlaceholder = document.createComment('shared-navigation');
			navigation.before(this._sharedNavigationPlaceholder);
			this._sharedNavigation = navigation;
			const siteNav = navigation.querySelector('[data-responsive-site-nav]');
			if ( siteNav ) {
				siteNav.classList.remove('style--classic');
				siteNav.classList.add('style--sidebar');
				siteNav.querySelectorAll('.submenu.normal-menu').forEach(menu=>{
					menu.style.removeProperty('left');
					menu.style.removeProperty('right');
				});
				siteNav.querySelectorAll('.submenu-masonry').forEach(menu=>{
					if ( typeof window.destroyMegaMasonry === 'function' ) {
						window.destroyMegaMasonry(menu);
					} else {
						menu.classList.remove('masonry-active');
						menu.style.removeProperty('height');
						Array.from(menu.children).forEach(item=>{
							item.style.removeProperty('width');
							item.style.removeProperty('transform');
						});
					}
				});
			}

			target.append(navigation);
			mobileNavigation.mountNavigation(navigation);
		}

		#restoreSharedNavigation(){
			if ( ! this._sharedNavigation || ! this._sharedNavigationPlaceholder ) return;
			const mobileNavigation = this.querySelector('mobile-navigation');
			mobileNavigation?.reset();
			const siteNav = this._sharedNavigation.querySelector('[data-responsive-site-nav]');
			if ( siteNav ) {
				siteNav.classList.remove('style--sidebar');
				siteNav.classList.add('style--classic');
			}
			this._sharedNavigationPlaceholder.replaceWith(this._sharedNavigation);
			this._sharedNavigationPlaceholder = null;
			this._sharedNavigation = null;
			if ( typeof window.runMegaMasonry === 'function' ) window.runMegaMasonry();
		}

		#setBackgroundInert(){
			this._inertedElements = [];
			const makeInert = elm=>{
				if ( ! elm || elm.hasAttribute('inert') ) return;
				elm.setAttribute('inert', '');
				this._inertedElements.push(elm);
			};

			(window.inertElems || []).forEach(elm=>{
				if ( elm.contains(this) ) {
					let branch = this;
					while ( branch && branch !== elm ) {
						const parent = branch.parentElement;
						if ( ! parent ) break;
						Array.from(parent.children).forEach(sibling=>{
							if ( sibling !== branch ) makeInert(sibling);
						});
						branch = parent;
					}
				} else if ( ! this.contains(elm) ) {
					makeInert(elm);
				}
			});
		}

		#restoreBackground(){
			this._inertedElements.forEach(elm=>elm.removeAttribute('inert'));
			this._inertedElements = [];
		}

		#closeImmediately(){
			this.opened = false;
			this.classList.remove('sidebar--opened');
			document.body.classList.remove('sidebar-opened', 'sidebar-opened--left', 'sidebar-opened--right', 'site-menu-opened');
			this.#restoreBackground();
			document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach(elm=>elm.setAttribute('aria-expanded', 'false'));
			this.querySelector('mobile-navigation')?.reset?.();
		}

		show(){
			if ( this.id === 'site-menu-sidebar' && this._navigationMedia?.matches ) return;
			clearTimeout(this._navigationHideTimer);
			this._navigationHideTimer = null;
			this.#mountSharedNavigation();
			this.opened = true;
			this.removeAttribute('aria-hidden');
			document.body.classList.add('sidebar-opened');
			if ( this.classList.contains('sidebar--right') ) {
				document.body.classList.add('sidebar-opened--right');
			} else if ( this.classList.contains('sidebar--left') ) {
				document.body.classList.add('sidebar-opened--left');
			}
			this.style.display = 'grid';
			setTimeout(()=>{
				if ( ! this.opened ) return;
				this.classList.add('sidebar--opened');
				this.#setBackgroundInert();
			}, 15);
			if ( this.id === "site-cart-sidebar" ) {
				const cartViewEvent = this.querySelector('[data-js-cart-view-event]');
				if ( cartViewEvent && typeof cartViewEvent.dispatchViewEvent === 'function' ) {
					cartViewEvent.dispatchViewEvent();
				}
				if ( document.querySelector('#cart-recommendations css-slider') ) {
					document.querySelector('#cart-recommendations css-slider').resetSlider();
				}
			}

		}

		hide(){
			this.#closeImmediately();
			this.setAttribute('aria-hidden', 'true');
			clearTimeout(this._navigationHideTimer);
			this._navigationHideTimer = setTimeout(()=>{
				this.style.display = 'none';
				this.#restoreSharedNavigation();
				this._navigationHideTimer = null;
			}, 501);
		}

		disconnectedCallback(){
			this._navigationMedia?.removeEventListener('change', this._navigationMediaChange);
			clearTimeout(this._navigationHideTimer);
			this.#restoreSharedNavigation();
		}

	}


  if ( typeof customElements.get('sidebar-drawer') === 'undefined' ) {
		customElements.define('sidebar-drawer', SidebarDrawer);
	}

}

if ( typeof MobileNavigation !== 'function' ) {
		
	class MobileNavigation extends HTMLElement {

		constructor() {

			super();

			this._openedFirstSubmenu = false;
			this._openedSecondSubmenu = false;
			this._mobileMedia = window.matchMedia('(max-width: 1023px)');

			if ( this.dataset.showHeaderActions === 'true' ) {
				const headerActions = document.querySelector('[data-js-header-actions]');
				const mobileNavActions = document.createElement('div');
				mobileNavActions.classList = "header-actions flex-buttons mobile-navigation-actions";
				mobileNavActions.innerHTML = headerActions ? headerActions.innerHTML : '';
				if ( mobileNavActions.innerHTML ) this.prepend(mobileNavActions);

				mobileNavActions.querySelectorAll('[id]').forEach(elm=>{
					elm.id = `${elm.id}-mobile`;
				});
				mobileNavActions.querySelectorAll('[data-modal]').forEach(elm=>{
					elm.addEventListener('keydown', e=>{
						if ( e.keyCode === window.KEYCODES.RETURN ) window.lastFocusedElm = elm;
					});
					elm.addEventListener('click', e=>{
						e.preventDefault();
						document.querySelector('.sidebar--opened')?.hide();
						document.getElementById(elm.getAttribute('aria-controls'))?.show();
					});
				});
			}
		}

		mountNavigation(navigation) {
			if ( ! navigation ) return;
			if ( this._boundNavigation === navigation ) {
				this.syncMode();
				return;
			}
			this._boundNavigation = navigation;

			navigation.querySelectorAll('.has-submenu > .menu-item__trigger > .submenu-toggle').forEach(button=>{
				button.addEventListener('click', e=>{
					if ( ! this._mobileMedia.matches || ! this.contains(button) ) return;
					e.preventDefault();
					this.#clearFirstLevel();
					this._openedFirstSubmenu = true;
					this.classList.add('opened-first-submenu');
					this.closest('sidebar-drawer').scrollTo({top: 0});
					button.closest('li').classList.add('opened');
					button.setAttribute('aria-expanded', 'true');
					this.#resizeContainer();
				})
			});

			navigation.querySelectorAll('.has-babymenu > .menu-item__trigger > .submenu-toggle').forEach(button=>{
				button.addEventListener('click', e=>{
					if ( ! this._mobileMedia.matches || ! this.contains(button) ) return;
					e.preventDefault();
					this.#clearSecondLevel();
					this._openedSecondSubmenu = true;
					this.classList.add('opened-second-submenu');
					this.closest('sidebar-drawer').scrollTo({top: 0});
					button.closest('li').classList.add('opened');
					button.setAttribute('aria-expanded', 'true');
					this.#resizeContainer();
				})
			});

			navigation.querySelectorAll('.submenu-back__button').forEach(button=>{
				button.addEventListener('click', e=>{
					if ( ! this._mobileMedia.matches || ! this.contains(button) ) return;
					const secondLevelItem = button.closest('.babymenu')?.closest('.has-babymenu');
					if ( this._openedSecondSubmenu && secondLevelItem ) {
						this.#clearSecondLevel();
						this.#resizeContainer();
					} else {
						this.#clearFirstLevel();
						this.#resizeContainer(true);
					}
					this.closest('sidebar-drawer').scrollTo({top: 0});
					e.preventDefault();
				})
			});

			this.syncMode();
		}

		syncMode(){
			const siteNav = this.querySelector('[data-responsive-site-nav]');
			if ( ! siteNav ) return;
			siteNav.classList.toggle('style--classic', ! this._mobileMedia.matches);
			siteNav.classList.toggle('style--sidebar', this._mobileMedia.matches);
			this.reset();
			if ( ! this._mobileMedia.matches ) {
				if ( typeof window.runMegaMasonry === 'function' ) window.runMegaMasonry();
			} else {
				siteNav.querySelectorAll('.submenu-masonry').forEach(menu=>{
					if ( typeof window.destroyMegaMasonry === 'function' ) {
						window.destroyMegaMasonry(menu);
					} else {
						menu.classList.remove('masonry-active');
						menu.style.removeProperty('height');
						Array.from(menu.children).forEach(item=>{
							item.style.removeProperty('width');
							item.style.removeProperty('transform');
						});
					}
				});
			}
		}

		reset(){
			this.#clearFirstLevel();
			this.style.height = 'auto';
			const linkList = this.querySelector('.link-list');
			if ( linkList ) {
				linkList.getAnimations().forEach(animation=>animation.cancel());
				linkList.style.removeProperty('animation');
				linkList.style.removeProperty('height');
				linkList.style.removeProperty('overflow');
			}
			this.querySelectorAll('li.focus').forEach(item=>item.classList.remove('focus'));
		}

		#clearSecondLevel(){
			this._openedSecondSubmenu = false;
			this.classList.remove('opened-second-submenu');
			this.querySelectorAll('li.has-babymenu.opened').forEach(item=>{
				item.classList.remove('opened');
				item.querySelector(':scope > .menu-item__trigger > .submenu-toggle')?.setAttribute('aria-expanded', 'false');
			});
		}

		#clearFirstLevel(){
			this.#clearSecondLevel();
			this._openedFirstSubmenu = false;
			this.classList.remove('opened-first-submenu');
			this.querySelectorAll('.submenu.normal-menu').forEach(menu=>{
				menu.style.removeProperty('left');
				menu.style.removeProperty('right');
			});
			this.querySelectorAll('li.has-submenu.opened').forEach(item=>{
				item.classList.remove('opened');
				item.querySelector(':scope > .menu-item__trigger > .submenu-toggle')?.setAttribute('aria-expanded', 'false');
			});
		}

		#resizeContainer(main=false){
      if ( main ) {
				this.style.height = `auto`;
      } else {
				if ( this._openedSecondSubmenu ) {
					const submenu = this.querySelector('.has-babymenu.opened .babymenu');
					if ( submenu ) this.style.height = `${submenu.scrollHeight}px`;
				} else if ( this._openedFirstSubmenu ) {
					const submenu = this.querySelector('.has-submenu.opened .submenu');
					if ( submenu ) this.style.height = `${submenu.scrollHeight}px`;
				}  
      }
		}

	}

  if ( typeof customElements.get('mobile-navigation') === 'undefined' ) {
		customElements.define('mobile-navigation', MobileNavigation);
	}

}

if ( typeof ScrollableNavigation !== 'function' ) {

	class ScrollableNavigation extends HTMLElement {

		constructor() {
			super();
			this.linkList = this.querySelector('.link-list');
			this.header = this.parentNode;
			this._resizeNavigation = debounce(()=>this.#checkNav(), 200);

			const rtl = document.documentElement.getAttribute('dir') === 'rtl';
			this.header?.querySelector('.scrollable-navigation-button--left')?.addEventListener('click', ()=>{
				this.scroll({ top: 0, left: this.scrollLeft - (rtl ? -100 : 100), behavior: 'smooth' });
			});
			this.header?.querySelector('.scrollable-navigation-button--right')?.addEventListener('click', ()=>{
				this.scroll({ top: 0, left: this.scrollLeft + (rtl ? -100 : 100), behavior: 'smooth' });
			});
		}

		connectedCallback(){
			window.addEventListener('resize', this._resizeNavigation);
			this.#checkNav();
		}

		#checkNav() {
			if ( ! this.linkList || ! this.header ) return;
			this.header.classList.toggle('scrolling-navigation-enabled', this.linkList.scrollWidth > this.offsetWidth);
		}

		disconnectedCallback(){
			window.removeEventListener('resize', this._resizeNavigation);
		}
	}

	if ( typeof customElements.get('scrollable-navigation') === 'undefined' ) {
		customElements.define('scrollable-navigation', ScrollableNavigation);
	}

}
